import React, { useEffect, useRef, memo, useCallback, useState, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Group, ImageIcon, RotateCw, Settings } from "lucide-react";
import { Image, Select, Tooltip } from "antd";
import { useAuth0 } from "@auth0/auth0-react";
import { isStaffEmail } from "@utils/user_utils";
import { MessageScroller, useMessageScroller } from "@shadcn/react/message-scroller";
import { useChatContext, Message } from "../context";
import { isAIMessage, ARTIFACT_VIZ_TYPES, AGENT_PART_TYPES, latestAgentRowById, pickedChoicesByRow, agentPartId, prefersReducedMotion } from "../utils";
import { getMessageClickAction } from "../utils/debugPanelUtils";
import ArtifactSkeleton from "./ArtifactSkeleton";
import MessageReview from "./MessageReview";
import ArtifactErrorBoundary from "./ArtifactErrorBoundary";
import SummaryCard from "./SummaryCard";
import InsightsCard from "./InsightsCard";
import StepsAccordion from "./StepsAccordion";
import AgentPart from "./AgentParts";
import CitationButton from "./CitationButton";
import type { InsightsData, MessageImage, AnswerReply, AgentPartData } from "../types";
import { useChatImage } from "../hooks/useChatImage";

const NEAR_BOTTOM_PX = 120;

// Progressive-reveal helpers. Reveal is a streaming affordance: it runs ONLY for the most
// recent (freshly streamed) artifact — see `animate` prop threaded from MessageList — so opening
// a past conversation doesn't replay every card at once. All of it collapses under
// prefers-reduced-motion. The big stat value count-up is the one JS-driven piece (CSS can't count);
// everything else is pure CSS in styles.css under the `chat-v2-artifact-animate` class.
const COUNT_UP_MS = 450;

const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);
// The count-up snaps to `target` on its final frame, which is correct only because the easing
// hits its endpoints exactly. Guard that invariant in dev so a future easing swap can't drift.
if (import.meta.env?.DEV) {
    console.assert(easeOutCubic(0) === 0 && easeOutCubic(1) === 1, "easeOutCubic must map 0->0 and 1->1");
}

// Animates a number 0 -> target with an ease-out. When disabled (not the freshly streamed card,
// deliberate-streaming off, reduced motion, or null/0 value) it returns the final value immediately,
// so the displayed number is always correct — only the path to it changes.
const useCountUp = (target: number | null, enabled: boolean): number | null => {
    const [display, setDisplay] = useState<number | null>(enabled ? 0 : target);
    useEffect(() => {
        if (!enabled || target == null || target === 0) {
            setDisplay(target);
            return;
        }
        let raf = 0;
        let start = 0;
        const tick = (now: number) => {
            if (!start) start = now;
            const t = Math.min((now - start) / COUNT_UP_MS, 1);
            // Snap to the exact target on the last frame rather than trusting float math to land on it.
            setDisplay(t >= 1 ? target : target * easeOutCubic(t));
            if (t < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [target, enabled]);
    return display;
};

type Cell = string | number;
type SortDir = "asc" | "desc" | null;

// Numeric if both cells parse as finite numbers, else string compare.
// ponytail: upgrade to per-column type detection if dates/mixed columns need it.
const compareCells = (a: Cell, b: Cell): number => {
    const na = a === "" || a == null ? NaN : Number(a);
    const nb = b === "" || b == null ? NaN : Number(b);
    if (!isNaN(na) && !isNaN(nb)) return na - nb;
    return String(a ?? "").localeCompare(String(b ?? ""));
};

const moveItem = <T,>(arr: T[], from: number, to: number): T[] => {
    const next = [...arr];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    return next;
};

// Faint on hover, solid + accent in the active sort direction. Both triangles always
// present so the header width never shifts between sorted/unsorted states.
const SortIcon: React.FC<{ dir: SortDir }> = ({ dir }) => (
    <svg className="chat-v2-sort-icon" width="7" height="10" viewBox="0 0 7 10" aria-hidden="true">
        <path className={`chat-v2-sort-up${dir === "asc" ? " on" : ""}`} d="M3.5 0 L7 4 L0 4 Z" />
        <path className={`chat-v2-sort-down${dir === "desc" ? " on" : ""}`} d="M3.5 10 L7 6 L0 6 Z" />
    </svg>
);

// ArtifactTable/Stat/Chart are also used by the dashboard AI Insights tile,
// which renders the same MessageParts outside the chat page. Keep them free of
// chat context.
export const ArtifactTable: React.FC<{ data: any; title?: string; animate?: boolean }> = ({ data, animate }) => {
    const headers: string[] = data?.headers ?? [];
    const rawRows: Cell[][] = data?.rows ?? [];

    // order holds original column indices in display order
    const [order, setOrder] = useState<number[]>(() => headers.map((_, i) => i));
    const [sort, setSort] = useState<{ col: number | null; dir: SortDir }>({ col: null, dir: null });
    const [groupBy, setGroupBy] = useState<number | null>(null);
    const [dragging, setDragging] = useState(false);
    const dragFrom = useRef<number | null>(null);

    const onSort = (col: number) =>
        setSort((s) =>
            s.col === col
                ? { col, dir: s.dir === "asc" ? "desc" : s.dir === "desc" ? null : "asc" }
                : { col, dir: "asc" });

    const onDrop = (toPos: number) => {
        setDragging(false);
        if (dragFrom.current === null || dragFrom.current === toPos) return;
        setOrder((o) => moveItem(o, dragFrom.current as number, toPos));
        dragFrom.current = null;
    };

    // rows in display order, plus the indices that begin a new group (for divider rows)
    const { rows, groupStarts } = useMemo(() => {
        let out = sort.dir && sort.col !== null
            ? [...rawRows].sort((r1, r2) => {
                const res = compareCells(r1[sort.col as number], r2[sort.col as number]);
                return sort.dir === "asc" ? res : -res;
            })
            : rawRows;
        const starts = new Set<number>();
        // group rows by the chosen column, groups in first-appearance order,
        // preserving the already-sorted order within each group
        if (groupBy !== null) {
            const groups = new Map<Cell, Cell[][]>();
            for (const row of out) {
                const key = row[groupBy];
                if (!groups.has(key)) groups.set(key, []);
                groups.get(key)!.push(row);
            }
            out = [...groups.values()].flat();
            let prev: Cell | undefined;
            out.forEach((row, i) => {
                if (i > 0 && row[groupBy] !== prev) starts.add(i);
                prev = row[groupBy];
            });
        }
        return { rows: out, groupStarts: starts };
    }, [rawRows, sort, groupBy]);

    // A column is groupable only if it has repeated non-numeric values — grouping a metric
    // column or an all-unique column (e.g. player name) is meaningless.
    const groupableCols = useMemo(() => {
        const cols: number[] = [];
        for (let c = 0; c < headers.length; c++) {
            const seen = new Set<Cell>();
            let hasRepeat = false;
            let allNumeric = true;
            for (const row of rawRows) {
                const v = row[c];
                if (v === "" || v == null || isNaN(Number(v))) allNumeric = false;
                if (seen.has(v)) hasRepeat = true;
                seen.add(v);
            }
            if (hasRepeat && !allNumeric) cols.push(c);
        }
        return cols;
    }, [headers, rawRows]);

    const isDefaultOrder = order.every((c, i) => c === i);
    const isModified = sort.dir !== null || groupBy !== null || !isDefaultOrder;
    const showToolbar = groupableCols.length > 0 || isModified;

    const reset = () => {
        setSort({ col: null, dir: null });
        setGroupBy(null);
        setOrder(headers.map((_, i) => i));
    };

    return (
        <>
            {showToolbar && (
                <div className="chat-v2-table-toolbar">
                    <button
                        type="button"
                        className={`chat-v2-table-reset${isModified ? "" : " hidden"}`}
                        onClick={reset}
                    >
                        <RotateCw size="1em" /> Reset
                    </button>
                    {groupableCols.length > 0 && (
                        <label className="chat-v2-table-groupby">
                            <Group size="1em" className="chat-v2-groupby-icon" />
                            <Select
                                size="small"
                                variant="borderless"
                                value={groupBy}
                                onChange={setGroupBy}
                                placeholder="Group by"
                                popupMatchSelectWidth={false}
                                options={[
                                    { value: null, label: "No grouping" },
                                    ...groupableCols.map((c) => ({ value: c, label: headers[c] })),
                                ]}
                            />
                        </label>
                    )}
                </div>
            )}
            <div className="chat-v2-table-scroll">
            <table className={`chat-v2-artifact-table${animate ? " chat-v2-artifact-animate" : ""}${dragging ? " chat-v2-table-dragging" : ""}`}>
                <thead>
                    <tr>
                        {order.map((col, pos) => (
                            <th
                                key={col}
                                className={`chat-v2-artifact-th${groupBy === col ? " grouped" : ""}`}
                                draggable
                                title="Click to sort · drag to reorder"
                                onClick={() => onSort(col)}
                                onDragStart={() => { dragFrom.current = pos; setDragging(true); }}
                                onDragEnd={() => setDragging(false)}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={() => onDrop(pos)}
                            >
                                <span className="chat-v2-th-inner">
                                    <span className="chat-v2-th-label">{headers[col]}</span>
                                    <SortIcon dir={sort.col === col ? sort.dir : null} />
                                </span>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row: Cell[], i: number) => (
                        // --i drives the staggered cascade; capped so a 200-row table doesn't take 10s.
                        <tr
                            key={i}
                            className={groupStarts.has(i) ? "chat-v2-row-group-start" : undefined}
                            style={{ "--i": Math.min(i, 12) } as React.CSSProperties}
                        >
                            {order.map((col) => <td key={col}>{row[col]}</td>)}
                        </tr>
                    ))}
                </tbody>
            </table>
            </div>
        </>
    );
};

export const ArtifactStat: React.FC<{ data: any; title?: string; animate?: boolean }> = ({ data, title, animate }) => {
    const {
        player_name, player_initials, value, metric_label, unit, date_range,
        delta_pct, context_stats = [],
    } = data ?? {};

    // Count-up runs for freshly-streamed cards, respecting reduced-motion (handled in useCountUp).
    const animatedValue = useCountUp(
        value ?? null,
        !!animate && !prefersReducedMotion(),
    );

    if (!data) return null;

    const fmt = (n: number | null) =>
        n == null ? "—" : n.toLocaleString(undefined, { maximumFractionDigits: 1 });
    const isPositive = (delta_pct ?? 0) > 0;

    const contextVals = context_stats.map((s: any) => s.value).filter((v: any) => v != null);
    const maxVal = Math.max(...(value != null ? [value] : []), ...contextVals) * 1.05 || 1;

    const barRows = [
        { label: "Player", val: value as number | null, cls: "chat-v2-stat-bar-player" },
        ...context_stats.map((s: any, i: number) => ({
            label: s.label,
            val: s.value as number | null,
            cls: i === 0 ? "chat-v2-stat-bar-team" : "chat-v2-stat-bar-pos",
        })),
    ];

    return (
        <div className={`chat-v2-stat-card${animate ? " chat-v2-artifact-animate" : ""}`}>
            <div className="chat-v2-stat-header">
                <span className="chat-v2-stat-title">{title}</span>
                <span className="chat-v2-stat-date">{date_range}</span>
            </div>

            <div className="chat-v2-stat-main">
                <div className="chat-v2-stat-avatar">{player_initials}</div>
                <div className="chat-v2-stat-player-info">
                    <span className="chat-v2-stat-player-name">{player_name}</span>
                    <span className="chat-v2-stat-metric-label">{metric_label}</span>
                </div>
                <div className="chat-v2-stat-value-block">
                    <span className="chat-v2-stat-value">{fmt(animatedValue)}{animatedValue != null ? unit : ""}</span>
                    {delta_pct != null && delta_pct !== 0 && (
                        <span className={`chat-v2-stat-delta ${isPositive ? "chat-v2-stat-delta-pos" : "chat-v2-stat-delta-neg"}`}>
                            {isPositive ? "▲" : "▼"} {Math.abs(delta_pct)}%
                        </span>
                    )}
                </div>
            </div>

            <div className="chat-v2-stat-bars">
                {barRows.map(({ label, val, cls }, i) => (
                    <div key={label} className="chat-v2-stat-bar-row" style={{ "--i": i } as React.CSSProperties}>
                        <span className="chat-v2-stat-bar-label">{label}</span>
                        <div className="chat-v2-stat-bar-track">
                            <div className={`chat-v2-stat-bar-fill ${cls}`} style={{ width: val != null ? `${(val / maxVal) * 100}%` : "0%" }} />
                        </div>
                        <span className="chat-v2-stat-bar-value">{fmt(val)}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export const ArtifactChart: React.FC<{ data: any; animate?: boolean }> = memo(({ data, animate }) => {
    // Highcharts draws series with its built-in animation on first render. Force it on for the
    // freshly streamed chart and off otherwise (history shouldn't redraw). Merge without mutating data.
    const options = useMemo(
        () => ({ ...data, plotOptions: { ...data?.plotOptions, series: { ...data?.plotOptions?.series, animation: !!animate } } }),
        [data, animate],
    );

    // Many categories cramp a responsive chart; give it a min-width so it scrolls instead.
    // ponytail: ~44px/category past 8 categories is a heuristic knob — tune if labels still crowd.
    const xAxis = Array.isArray(data?.xAxis) ? data.xAxis[0] : data?.xAxis;
    const catCount = xAxis?.categories?.length
        ?? Math.max(0, ...(data?.series?.map((s: any) => s?.data?.length ?? 0) ?? [0]));
    const minWidth = catCount > 8 ? catCount * 44 : undefined;

    return (
        <div className="chat-v2-chart-scroll">
            <div style={{ minWidth }}>
                <HighchartsReact highcharts={Highcharts} options={options} />
            </div>
        </div>
    );
});

const ArtifactContent: React.FC<{ message: Message; animate?: boolean }> = ({ message, animate }) => {
    const data = message.metadata.data;
    const title = message.metadata.title as string | undefined;
    if (message.type === "table") return <ArtifactTable data={data} title={title} animate={animate} />;
    if (message.type === "stat")  return <ArtifactStat  data={data} title={title} animate={animate} />;
    if (message.type === "chart") return <ArtifactChart data={data} animate={animate} />;
    return null;
};

// A late image load or a broken placeholder changes the bubble height after mount. The
// MessageScroller content ResizeObserver re-pins a reader at the bottom, so no onLoad hook here.
const ChatImage: React.FC<{ image: MessageImage }> = ({ image }) => {
    const src = useChatImage(image.ref); // undefined = loading, null = failed, string = ready
    if (src === undefined) return <div className="chat-v2-image-loading" />;
    if (src === null) {
        return (
            <div className="chat-v2-image-broken" title={image.fileName}>
                <ImageIcon size={20} />
                <span>Couldn't load this image</span>
            </div>
        );
    }
    return (
        <Image className="chat-v2-chat-image" src={src} alt={image.fileName ?? "attached image"} />
    );
};

// A committed row is the turn moving on, so it always pins to the bottom, even for a reader who
// scrolled up. MessageScroller follows only a reader at the bottom, so this rule lives here.
// It renders nothing: useMessageScroller() works only inside the Provider.
const PinOnCommit: React.FC<{ messages: Message[] }> = ({ messages }) => {
    const { scrollToEnd } = useMessageScroller();
    // Keyed on messages only: if scrollToEnd ever changed identity during a stream, a dependency
    // on it would pin a reader who scrolled up on every streaming tick.
    useEffect(() => {
        scrollToEnd({ behavior: "smooth" });
    }, [messages]); // eslint-disable-line react-hooks/exhaustive-deps
    return null;
};

// A WhistleAgent card, question or result. No bubble click handler: a click on a chip must not
// copy the text.
const AgentRow: React.FC<{ message: Message; isOpen: boolean; isLive: boolean; picked: string[]; onAnswer: (reply: AnswerReply) => void; actions: React.ReactNode }> =
    ({ message, isOpen, isLive, picked, onAnswer, actions }) => (
        <div className="chat-v2-message-turn">
            <ArtifactErrorBoundary label={message.type}>
                <AgentPart message={message} isOpen={isOpen} isLive={isLive} picked={picked} onAnswer={onAnswer} />
            </ArtifactErrorBoundary>
            {actions}
        </div>
    );

// The AI rows of each turn, keyed by the id of the turn's last row. That row carries the one
// action bar of the turn. A row without a turnId is a turn of its own.
function turnRowsByLastRowId(messages: Message[]): Map<Message["id"], Message[]> {
    const byTurn = new Map<string, Message[]>();
    for (const m of messages) {
        if (!isAIMessage(m) || m.type === "steps" || m.created === 0) continue;
        const key = String(m.metadata?.turnId ?? `row-${m.id}`);
        const rows = byTurn.get(key);
        if (rows) rows.push(m);
        else byTurn.set(key, [m]);
    }
    return new Map([...byTurn.values()].map(rows => [rows[rows.length - 1].id, rows]));
}

interface MessageListProps {
    // Hands a chip click or a multi-choice Send to the composer, which owns the send.
    onAnswer?: (reply: AnswerReply) => void;
    // Only a host with the "Tool calls" drawer sets this. The floating widget has no drawer.
    showToolCalls?: boolean;
}

const MessageList: React.FC<MessageListProps> = ({ onAnswer = () => {}, showToolCalls = false }) => {
    const { messages, openQuestionIds, isTyping, streamingCaption, pendingText, pendingVizType, selectedMessage, setSelectedMessage, setDrawerMode, stepsByTurn, activeTurnId } = useChatContext();
    const { user } = useAuth0();
    const canShowToolCalls = showToolCalls && isStaffEmail(user?.email);
    const [copiedId, setCopiedId] = useState<string | number | null>(null);

    // A row is open for an open id, on the latest card or question row of that id. It is live
    // while no turn streams too. A `failed` result keeps its card open, so the coach can retry.
    const latestAgentRows = useMemo(() => latestAgentRowById(messages), [messages]);
    const pickedByRow = useMemo(() => pickedChoicesByRow(messages), [messages]);
    const isAgentRowOpen = (message: Message): boolean => {
        const data = message.metadata.data as AgentPartData | null | undefined;
        if (!data || message.type === "action_result") return false;
        const id = agentPartId(data);
        return openQuestionIds.includes(id) && latestAgentRows.get(id) === message;
    };

    const handleToolCallsClick = useCallback((msg: Message) => {
        const action = getMessageClickAction(msg.id, selectedMessage?.id ?? null);
        if (action === "select") {
            setSelectedMessage(msg);
            setDrawerMode("message");
        } else {
            setSelectedMessage(null);
            setDrawerMode(null);
        }
    }, [selectedMessage, setSelectedMessage, setDrawerMode]);

    const handleBubbleCopy = (msg: Message) => {
        navigator.clipboard.writeText(msg.content ?? "").then(() => {
            setCopiedId(msg.id);
            setTimeout(() => setCopiedId(null), 2000);
        });
    };

    const copyableBubble = (msg: Message, bubble: React.ReactElement) => (
        <Tooltip
            title={copiedId === msg.id ? "Copied!" : "Copy text"}
            placement={isAIMessage(msg) ? "bottomLeft" : "bottomRight"}
            overlayClassName="chat-v2-tooltip-sm"
            mouseEnterDelay={0.15}
        >
            {bubble}
        </Tooltip>
    );

    const turnRows = useMemo(() => turnRowsByLastRowId(messages), [messages]);
    // One action bar per turn, under its last AI row. It waits for the end of a live turn, so
    // that a review covers the full answer.
    const turnActions = (msg: Message) => {
        const rows = turnRows.get(msg.id);
        if (!rows || (isTyping && msg.metadata.turnId === activeTurnId)) return null;
        const toolCallsOpen = selectedMessage?.id === msg.id;
        return (
            <div className="chat-v2-message-actions">
                <MessageReview aiMessages={rows} />
                {canShowToolCalls && (
                    <Tooltip title="Tool calls" placement="bottom" overlayClassName="chat-v2-tooltip-sm">
                        <button
                            type="button"
                            className={`chat-v2-review-btn${toolCallsOpen ? " chat-v2-review-btn-active" : ""}`}
                            aria-label="Tool calls"
                            aria-pressed={toolCallsOpen}
                            onClick={() => handleToolCallsClick(msg)}
                        >
                            <Settings size={14} />
                        </button>
                    </Tooltip>
                )}
            </div>
        );
    };

    // The turns that already have an answer row on screen, artifacts included. The peek window
    // under a trail closes as soon as its turn appears here. Built once per render rather than
    // scanned per trail: during a reasoning drip this list re-renders about 80 times a second.
    const answeredTurnIds = useMemo(() => new Set(
        messages.filter(m => isAIMessage(m) && m.type !== "steps" && m.metadata?.turnId != null)
            .map(m => m.metadata.turnId as string),
    ), [messages]);

    // MessageScroller owns the scroll position. Streaming growth (the reasoning drip, captions,
    // late images) follows only a reader within NEAR_BOTTOM_PX of the bottom; a wheel, touch,
    // scroll key or scroll-up releases the follow. Three rules keep our layout and screen-reader
    // output as they were:
    // - No MessageScroller.Root: it is a bare div, and .chat-v2-messages-container must stay the
    //   direct flex child of its host, or it loses flex: 1 and stops scrolling.
    // - No MessageScroller.Item: a block wrapper cancels align-self on the AI bubbles. Rows
    //   without a messageId are skipped by the scroller, and we use none of what Item adds.
    // - aria-live="off": Content defaults to role="log", which would read the whole history on
    //   each conversation load. StepsAccordion's role="status" stays the only announcer.
    return (
        <MessageScroller.Provider autoScroll scrollEdgeThreshold={NEAR_BOTTOM_PX}>
            <MessageScroller.Viewport className="chat-v2-messages-container">
            <MessageScroller.Content className="chat-v2-messages-inner" aria-live="off">
                <PinOnCommit messages={messages} />
                {messages.map((message) => {
                    // Reveal runs only for messages that arrived live from the stream (message.fresh),
                    // never on conversation load.
                    const animate = !!message.fresh;
                    const isCopied = copiedId === message.id;

                    if (message.type === "steps") {
                        const turnId = message.metadata.turnId;
                        const answerStarted = Boolean(streamingCaption) || Boolean(pendingText)
                            || (turnId != null && answeredTurnIds.has(turnId));
                        return (
                            // No bubble click handler: every click inside this row is the
                            // accordion's own expand/collapse.
                            <div key={message.id} className="chat-v2-message-turn">
                                <ArtifactErrorBoundary label="steps">
                                    <StepsAccordion
                                        steps={(turnId && stepsByTurn[turnId]) || []}
                                        isLive={isTyping && turnId === activeTurnId}
                                        answerStarted={answerStarted}
                                    />
                                </ArtifactErrorBoundary>
                            </div>
                        );
                    }

                    if (AGENT_PART_TYPES.has(message.type)) {
                        const isOpen = isAgentRowOpen(message);
                        return <AgentRow key={message.id} message={message} isOpen={isOpen} isLive={isOpen && !isTyping} picked={pickedByRow.get(message.id) ?? []} onAnswer={onAnswer} actions={turnActions(message)} />;
                    }

                    if (message.type === "summary") {
                        return (
                            <div key={message.id} className="chat-v2-message-turn">
                                <ArtifactErrorBoundary label="summary">
                                    <SummaryCard payload={message.metadata.data} animate={animate} citations={message.metadata?.citations} />
                                </ArtifactErrorBoundary>
                                {turnActions(message)}
                            </div>
                        );
                    }

                    if (message.type === "insights") {
                        const insightsData = message.metadata.data as InsightsData | null | undefined;
                        return (
                            <div key={message.id} className="chat-v2-message-turn">
                                <ArtifactErrorBoundary label="insights">
                                    <InsightsCard data={insightsData} animate={animate} />
                                </ArtifactErrorBoundary>
                                {turnActions(message)}
                            </div>
                        );
                    }

                    if (ARTIFACT_VIZ_TYPES.has(message.type)) {
                        return (
                            <div key={message.id} className="chat-v2-artifact-group">
                                {message.content && copyableBubble(message, (
                                    <div
                                        className={`chat-v2-bubble chat-v2-bubble-ai chat-v2-bubble-copyable${isCopied ? " chat-v2-bubble-copied" : ""}`}
                                        onClick={() => handleBubbleCopy(message)}
                                    >
                                        <ReactMarkdown>{message.content}</ReactMarkdown>
                                        <CitationButton citations={message.metadata?.citations} />
                                    </div>
                                ))}
                                <div className="chat-v2-bubble chat-v2-bubble-ai">
                                    <ArtifactErrorBoundary label={message.type}>
                                        <ArtifactContent message={message} animate={animate} />
                                    </ArtifactErrorBoundary>
                                    {/* Citations normally land on the caption bubble above — this only
                                        renders for the fallback case of an artifact-only turn with no caption. */}
                                    {!message.content && <CitationButton citations={message.metadata?.citations} />}
                                </div>
                                {turnActions(message)}
                            </div>
                        );
                    }

                    const isUserMessage = !isAIMessage(message);
                    const bubble = (
                        <div
                            className={[
                                "chat-v2-bubble",
                                isUserMessage ? "chat-v2-bubble-user" : "chat-v2-bubble-ai",
                                "chat-v2-bubble-copyable",
                                isCopied ? "chat-v2-bubble-copied" : "",
                            ].filter(Boolean).join(" ")}
                            onClick={() => handleBubbleCopy(message)}
                        >
                            {isUserMessage && message.metadata.images?.length ? (
                                // Clicking an image should open the preview modal, not trigger the
                                // bubble's copy-on-click — stop the click from bubbling up.
                                <div className="chat-v2-image-row" onClick={e => e.stopPropagation()}>
                                    <Image.PreviewGroup>
                                        {message.metadata.images.map(img => (
                                            <ChatImage key={img.ref} image={img} />
                                        ))}
                                    </Image.PreviewGroup>
                                </div>
                            ) : null}
                            <ReactMarkdown>{message.content ?? ""}</ReactMarkdown>
                            {isAIMessage(message) && <CitationButton citations={message.metadata?.citations} />}
                        </div>
                    );
                    return (
                        <div key={message.id} className="chat-v2-message-turn">
                            {copyableBubble(message, bubble)}
                            {turnActions(message)}
                        </div>
                    );
                })}
                {streamingCaption && (
                    <div className="chat-v2-bubble chat-v2-bubble-ai">
                        {streamingCaption}
                    </div>
                )}
                {pendingText && (
                    <div className="chat-v2-bubble chat-v2-bubble-ai">
                        <ReactMarkdown>{pendingText}</ReactMarkdown>
                    </div>
                )}
                {pendingVizType && (
                    pendingVizType === "insights" ? (
                        <ArtifactSkeleton type={pendingVizType} />
                    ) : (
                        <div className="chat-v2-bubble chat-v2-bubble-ai">
                            <ArtifactSkeleton type={pendingVizType} />
                        </div>
                    )
                )}
                {isTyping && !activeTurnId && !streamingCaption && !pendingText && !pendingVizType && (
                    <StepsAccordion steps={[]} isLive />
                )}
            </MessageScroller.Content>
            </MessageScroller.Viewport>
        </MessageScroller.Provider>
    );
};

export default MessageList;


